import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage
} from '@/components/ui/form';
import { useCreateOrganisation } from '@/services/organisations/queries';
import { useNavigate } from 'react-router-dom';
import { Heading } from '@/components/ui/heading';
import { showToast } from '@/components/ui/show-toast';
import { useAuthStore } from '@/store/auth';
import * as z from 'zod';
import { cleanDeep } from '@/lib/utils';
import { BulkUploadModal } from './bulk-upload-modal';

// Organization form schema
const organisationSchema = z.object({
  name: z.string().min(1, 'Organization name is required'),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: z.string().optional(),
  address: z.string().optional(),
  website: z.string().url('Please enter a valid URL').optional().or(z.literal('')),
  linkedInUrl: z.string().url('Please enter a valid LinkedIn URL').optional().or(z.literal('')),
  headOfficeLocation: z.string().optional(),
  bio: z.string().optional(),
  mission: z.string().optional(),
  yearFounded: z.string().optional(),
  contactPerson: z.object({
    name: z.string().min(1, "Contact person's name is required"),
    email: z.string().email('Please enter a valid contact person email'),
    bio: z.string().optional(),
    linkedInUrl: z.string().url('Please enter a valid LinkedIn URL').optional().or(z.literal(''))
  })
});

type OrganisationFormValues = z.infer<typeof organisationSchema>;

export default function OrganisationOnboardingForm() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const navigate = useNavigate();
  const activeWorkspace = useAuthStore(state => state.activeWorkspace);
  
  const form = useForm<OrganisationFormValues>({
    resolver: zodResolver(organisationSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      address: '',
      website: '',
      linkedInUrl: '',
      headOfficeLocation: '',
      bio: '',
      mission: '',
      yearFounded: '',
      contactPerson: {
        name: '',
        email: '',
        bio: '',
        linkedInUrl: ''
      }
    }
  });

  const createOrganisationMutation = useCreateOrganisation(
    () => {
      showToast('success', 'Organisation created successfully');
      setIsSubmitted(true);
    },
    (error: any) => {
      showToast('error', error.response.data.message || 'Failed to create organisation');
      console.error('Create organisation error:', error);
    }
  );

  const onSubmit = async (data: OrganisationFormValues) => {
    const cleanData = cleanDeep(data);
    const payload = {
      ...cleanData,
      yearFounded: cleanData.yearFounded ? new Date(cleanData.yearFounded) : undefined,
      workspaceIds: [activeWorkspace]
    }
    createOrganisationMutation.mutate(payload);
  };

  if (isSubmitted) {
    return (
      <Card className='mx-auto mt-10 flex h-full min-h-[85svh] w-full items-center justify-center bg-white p-6'>
        <CardContent className='flex flex-col items-center justify-center pt-6'>
          <div className='mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100'>
            <div className='flex h-12 w-12 items-center justify-center rounded-full bg-green-500'>
              <Check className='h-6 w-6 text-white' />
            </div>
          </div>

          <h2 className='mb-2 text-center text-2xl font-bold'>
            Organisation Created Successfully
          </h2>
          <div className='w-full max-w-[350px]'>
            <p className='mb-8 text-center text-muted-foreground'>
              The organisation has been created and added to the system.
            </p>
          </div>

          <Button className='w-full' asChild>
            <Link to='/dashboard/organisations'>Go back to organisations</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-5xl px-4 mt-10">
      <div className="flex items-center justify-between mb-8">
        <Heading 
          title="Add New Organisation" 
          description="Create a new organisation and add it to the system"
        />
        <div className="flex items-center gap-3">
          <BulkUploadModal />
        </div>
      </div>

      <Card className="bg-white shadow-sm">
        <CardContent className="p-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
              {/* Organisation Details Section */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Organisation Details</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Organisation Name *</label>
                        <FormControl>
                          <Input
                            placeholder="Enter organisation name"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Organisation Email *</label>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter organisation email"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Phone Number</label>
                        <FormControl>
                          <Input
                            placeholder="Enter phone number"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="website"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Website</label>
                        <FormControl>
                          <Input
                            placeholder="https://example.com"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Address</label>
                      <FormControl>
                        <Input
                          placeholder="Enter organisation address"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="headOfficeLocation"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Head Office Location</label>
                      <FormControl>
                        <Input
                          placeholder="Abuja, Nigeria"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="linkedInUrl"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">LinkedIn URL</label>
                        <FormControl>
                          <Input
                            placeholder="https://linkedin.com/company/example"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="yearFounded"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Year Founded</label>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="e.g., 2020"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Bio</label>
                        <FormControl>
                          <Textarea
                            placeholder="Enter organisation bio"
                            rows={4}
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mission"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Mission</label>
                        <FormControl>
                          <Textarea
                            placeholder="Enter organisation mission"
                            rows={4}
                            className="resize-none"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              {/* Contact Person Section */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Contact Person Details</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="contactPerson.name"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Contact Person Name *</label>
                        <FormControl>
                          <Input
                            placeholder="Enter contact person name"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="contactPerson.email"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Contact Person Email *</label>
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="Enter contact person email"
                            className="h-11"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="contactPerson.linkedInUrl"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Contact Person LinkedIn</label>
                      <FormControl>
                        <Input
                          placeholder="https://linkedin.com/in/example"
                          className="h-11"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactPerson.bio"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <label className="text-sm font-medium text-gray-700">Contact Person Bio</label>
                      <FormControl>
                        <Textarea
                          placeholder="Enter contact person bio"
                          rows={4}
                          className="resize-none"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-16 pt-8 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="px-6"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={createOrganisationMutation.isPending}
                  className="px-6"
                >
                  {createOrganisationMutation.isPending ? 'Creating...' : 'Create Organisation'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
